import React, { useEffect, useRef, useState } from 'react'
import '../../components/css/JobSearchBar.css';
import { JobSearchBy, JOB_SEARCH_BY_LABELS, JOB_SEARCH_BY_PLACEHOLDERS } from '../../data/enums/jobEnums';

interface IJobsSearchBarProps {
    isMobile: boolean;
    // VI-375: onSearch reports which field the user chose to search by.
    onSearch: (searchTerm: string, searchBy: JobSearchBy) => void;
    initialSearchTerm?: string;
    initialSearchBy?: JobSearchBy;
}

// Figma tokens (node 2002:11865 / 2002:11869)
const BORDER = '#E4E6EF';        // Utility/Border Color
const SELECTOR_BG = '#F3F6F9';   // Fill/Light
const BODY_TEXT = '#3F4254';     // Utility/Body Text
const LABEL_TEXT = '#343A40';    // Gray/800
const PRIMARY = '#2F86DD';       // Primary/Default
const CONTAINER_BG = '#F8F9FA';  // Gray/100

const JobsSearchBar = ({ isMobile, onSearch, initialSearchTerm = '', initialSearchBy = JobSearchBy.JOB_TITLE }: IJobsSearchBarProps) => {

    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    // VI-375 AC #2: "Job Title" is the default option.
    const [selectedSearchBy, setSelectedSearchBy] = useState<JobSearchBy>(initialSearchBy);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const typeRef = useRef<HTMLDivElement>(null);

    // Sync internal state with props when they change (e.g., navigating back to Jobs tab)
    useEffect(() => {
        setSearchTerm(initialSearchTerm);
    }, [initialSearchTerm]);

    useEffect(() => {
        setSelectedSearchBy(initialSearchBy);
    }, [initialSearchBy]);

    // Close the search-by menu on outside click or Escape.
    useEffect(() => {
        if (!isMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (typeRef.current && !typeRef.current.contains(e.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isMenuOpen]);

    const handleApply = () => {
        onSearch(searchTerm, selectedSearchBy);
    };

    const handleSelect = (field: JobSearchBy) => {
        setSelectedSearchBy(field);
        setIsMenuOpen(false);
    };

    // VI-375 AC #2-4: placeholder reflects the selected search-by field.
    const searchPlaceholder = JOB_SEARCH_BY_PLACEHOLDERS[selectedSearchBy];

    return (
        <div
            style={{
                width: '100%',
                backgroundColor: CONTAINER_BG,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'stretch',
                gap: '0.5rem',
                borderRadius: '8px',
                padding: '1rem',
                boxSizing: 'border-box',
            }}
        >
            {/* Label + unified search-by / search control */}
            <div
                style={{
                    flex: isMobile ? 'auto' : 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    justifyContent: 'flex-end',
                }}
            >
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: LABEL_TEXT, lineHeight: '21px', margin: 0 }}>
                    Search
                </p>

                <div
                    className={`jsb-control${isFocused ? ' is-focused' : ''}`}
                    style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        width: '100%',
                        border: `1px solid ${BORDER}`,
                        borderRadius: '4px',
                        backgroundColor: '#FFFFFF',
                    }}
                >
                    {/* Search-by selector (compact grey segment) */}
                    <div ref={typeRef} style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                            type="button"
                            className="jsb-type-trigger"
                            aria-haspopup="listbox"
                            aria-expanded={isMenuOpen}
                            onClick={() => setIsMenuOpen(open => !open)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                height: '38px',
                                padding: '6px 12px',
                                backgroundColor: SELECTOR_BG,
                                border: 'none',
                                borderRight: `1px solid ${BORDER}`,
                                borderRadius: '4px 0 0 4px',
                                color: BODY_TEXT,
                                fontSize: '14px',
                                lineHeight: '24px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <span>{JOB_SEARCH_BY_LABELS[selectedSearchBy]}</span>
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                aria-hidden="true"
                                style={{ transform: isMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.12s ease', flexShrink: 0 }}
                            >
                                <path d="M2.75 4.5L6 7.75L9.25 4.5" stroke={BODY_TEXT} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {isMenuOpen && (
                            <ul
                                role="listbox"
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    minWidth: '180px',
                                    margin: 0,
                                    padding: '4px',
                                    listStyle: 'none',
                                    backgroundColor: '#FFFFFF',
                                    border: `1px solid ${BORDER}`,
                                    borderRadius: '6px',
                                    boxShadow: '0 6px 18px rgba(13, 45, 77, 0.12)',
                                    zIndex: 20,
                                }}
                            >
                                {Object.values(JobSearchBy).map(field => (
                                    <li
                                        key={field}
                                        role="option"
                                        aria-selected={field === selectedSearchBy}
                                        className={`jsb-menu-item${field === selectedSearchBy ? ' is-selected' : ''}`}
                                        onClick={() => handleSelect(field)}
                                        // Text color is set via CSS (.jsb-menu-item / .is-selected) so the
                                        // selected item's white text isn't overridden by an inline style.
                                        style={{ padding: '8px 12px', fontSize: '14px', lineHeight: '22px', borderRadius: '4px' }}
                                    >
                                        {JOB_SEARCH_BY_LABELS[field]}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Search input (white segment) */}
                    <input
                        id="searchJobTitle"
                        name="searchJobTitle"
                        type="text"
                        className="jsb-input"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                            setTimeout(() => {
                                if (e.target instanceof HTMLInputElement) {
                                    setSearchTerm(e.target.value);
                                }
                            }, 0);
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                handleApply();
                            }
                        }}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: '38px',
                            padding: '6px 12px',
                            border: 'none',
                            outline: 'none',
                            backgroundColor: '#FFFFFF',
                            color: BODY_TEXT,
                            fontSize: '14px',
                            lineHeight: '24px',
                            borderRadius: '0 4px 4px 0',
                        }}
                    />
                </div>
            </div>

            {/* Apply button (bottom-aligned with the control) */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    flexShrink: 0,
                    width: isMobile ? '100%' : '92px',
                }}
            >
                <button
                    type="button"
                    onClick={handleApply}
                    style={{
                        width: '100%',
                        height: '38px',
                        backgroundColor: PRIMARY,
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 16px',
                        fontSize: '14px',
                        cursor: 'pointer',
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    )
}

export default JobsSearchBar